import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Sparkles, ExternalLink, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function WebsiteBuilder() {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/website/me").then((r) => setSite(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/website", {});
      setSite(data);
      toast.success("Website generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const publicUrl = site ? `${window.location.origin}/s/${site.slug}` : "";
  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Site URL copied");
  };

  if (loading)
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );

  return (
    <div className="space-y-8" data-testid="website-builder-root">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">AI Website Builder</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Your AI-built site</h1>
          <p className="text-white/50 mt-2 max-w-2xl text-sm">
            Complete marketing website — hero, features, about, services, testimonials, FAQ, contact — generated and live.
          </p>
        </div>
        <button
          onClick={generate}
          data-testid="website-generate-button"
          disabled={generating}
          className="inline-flex items-center gap-2 brand-gradient px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 glow-orange"
        >
          {site ? <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} /> : <Sparkles className="h-4 w-4" />}
          {generating ? "Generating…" : site ? "Regenerate site" : "Generate my website"}
        </button>
      </header>

      {!site ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Globe className="h-10 w-10 mx-auto text-white/30 mb-4" />
          <div className="text-white/50 max-w-md mx-auto">
            No site yet. Click <span className="text-white">Generate my website</span> — the AI uses your business
            profile and brand identity to write the entire site.
          </div>
        </div>
      ) : (
        <>
          <div className="glass rounded-2xl p-6 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/40">Public URL</div>
              <div className="font-mono text-sm mt-1 break-all" data-testid="website-public-url">
                {publicUrl}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={copyUrl} data-testid="website-copy-url" className="glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center gap-2">
                <Copy className="h-4 w-4" /> Copy
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="website-open"
                className="brand-gradient px-4 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90"
              >
                Open site <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          <SitePreview site={site.site} brandColor={site.brand_color} />
        </>
      )}
    </div>
  );
}

function SitePreview({ site, brandColor }) {
  if (!site) return null;
  const Section = ({ title, children }) => (
    <div className="glass rounded-2xl p-6">
      <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">{title}</div>
      {children}
    </div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {site.hero && (
        <Section title="Hero">
          <div className="text-xs font-mono mb-2" style={{ color: brandColor }}>{site.hero.eyebrow}</div>
          <h2 className="font-heading text-2xl font-black">{site.hero.headline}</h2>
          <p className="text-sm text-white/70 mt-2">{site.hero.subheadline}</p>
          <div className="mt-3 flex gap-2 text-xs">
            <span className="px-3 py-1 rounded" style={{ background: brandColor }}>{site.hero.primary_cta}</span>
            <span className="px-3 py-1 rounded glass">{site.hero.secondary_cta}</span>
          </div>
        </Section>
      )}
      {Array.isArray(site.features) && (
        <Section title="Features">
          <ul className="space-y-2 text-sm">
            {site.features.map((f, i) => (
              <li key={i}>
                <span className="font-semibold">{f.title}</span> — <span className="text-white/60">{f.description}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {site.about && (
        <Section title="About">
          <div className="font-heading text-lg font-bold">{site.about.title}</div>
          <p className="text-sm text-white/70 mt-2">{site.about.story}</p>
        </Section>
      )}
      {Array.isArray(site.services) && (
        <Section title="Services">
          <ul className="space-y-2 text-sm">
            {site.services.map((s, i) => (
              <li key={i}>
                <span className="font-semibold">{s.name}</span> — <span className="text-white/60">{s.price_hint}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {Array.isArray(site.faq) && (
        <Section title={`FAQ (${site.faq.length})`}>
          <ul className="space-y-2 text-sm text-white/70">
            {site.faq.slice(0, 3).map((q, i) => (
              <li key={i}>· {q.question}</li>
            ))}
          </ul>
        </Section>
      )}
      {site.contact && (
        <Section title="Contact">
          <div className="text-sm text-white/70">{site.contact.email}</div>
          <div className="text-sm text-white/70">{site.contact.phone}</div>
        </Section>
      )}
    </div>
  );
}
